import { useState, useEffect } from 'react'
import { ArrowLeft, Copy, Share2, QrCode, X, Zap, TrendingUp, Sparkles } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { getProfile, getReferralStats } from '../../services/userService'
import { useToast } from '../../context/ToastContext'
import Layout from '../../components/Layout/Layout'
import '../../styles/referral.css'

interface ReferralRow {
    uid: string | number
    email: string
    activationDate: string
    transactionAmount: number
    transactionFee: number
    rebateRatio: number
    rebateAmount: number
}

const RULES = [
    "The inviting relationship is established based on the invitee's account registration time through the invitation link, with data updated hourly.",
    "To complete the invitation relationship binding, the invitee must sign up using the inviter's invitation code, link, or QR code.",
    "Commissions generated from trades on Day T (UTC+0) will be distributed after 1:00 AM on T+1 (UTC+0). Commissions are automatically converted to USDT and credited to your wallet. Due to settlement processes, actual distribution may be delayed.",
    "To ensure accuracy and security, rebate data is only available from September 2026 onwards.",
    "If an account is found to be engaged in any fraudulent activity — including but not limited to fake registrations, self-invitations using multiple accounts, or falsified identity information — the platform reserves the right to disqualify the user from participating and confiscate any illicit rewards.",
    "Due to market fluctuations and platform policy updates, referral rules, commission rates, and reward structures may be adjusted at Bicoin's discretion. Please refer to the latest in-app information for current terms.",
]

const FEATURES = [
    { icon: Zap, title: 'Real-time Updates', desc: 'Daily & hourly commission payouts and withdrawals' },
    { icon: TrendingUp, title: 'Trading History', desc: 'Trackable history & commission earnings' },
    { icon: Sparkles, title: 'Custom Referral Visuals', desc: 'Custom referral links & code' },
]

export default function Referral() {
    const navigate = useNavigate()
    const { toast } = useToast()
    const [profile, setProfile] = useState<any>(null)
    const [stats, setStats] = useState<ReferralRow[]>([])
    const [loading, setLoading] = useState(true)
    const [showQrModal, setShowQrModal] = useState(false)
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')

    useEffect(() => {
        getProfile().then(setProfile).catch(() => { })
        getReferralStats()
            .then(data => setStats(Array.isArray(data) ? data : []))
            .catch(() => setStats([]))
            .finally(() => setLoading(false))
    }, [])

    // Sharing your account UID here would silently never link referrals — the backend
    // matches new signups against `invitationCode`, a separate auto-generated field.
    const referralCode = profile?.invitationCode ? String(profile.invitationCode) : '----------'
    const referralLink = `${window.location.origin}/signup?ref=${referralCode}`

    const filteredStats = stats.filter(row => {
        if (!startDate && !endDate) return true
        const d = new Date(row.activationDate)
        if (startDate && d < new Date(startDate)) return false
        if (endDate && d > new Date(endDate + 'T23:59:59')) return false
        return true
    })

    const totalInvitees = stats.length
    const totalRebateAmount = stats.reduce((sum, r) => sum + (Number(r.rebateAmount) || 0), 0)

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text)
        toast(`${label} copied`, 'success')
    }

    const shareLink = async () => {
        if (navigator.share) {
            try {
                await navigator.share({ title: 'Join Bicoin', text: 'Sign up on Bicoin using my referral link', url: referralLink })
            } catch { /* user cancelled — no action needed */ }
        } else {
            copyToClipboard(referralLink, 'Referral link')
        }
    }

    return (
        <Layout activePage="referral" hideMobileNav={true}>
            <div className="referral-page">
                {/* Header */}
                <div className="referral-top-bar">
                    <button className="referral-back-btn" onClick={() => navigate(-1)}>
                        <ArrowLeft size={20} />
                    </button>
                    <span className="referral-top-title">Bicoin | Referral</span>
                </div>

                <div className="referral-scroll">
                    {/* Hero */}
                    <div className="referral-hero">
                        <h1>Invite friends, earn together</h1>
                        <p>Share Bicoin with your network and share in the growth benefits of Bicoin — earn a rebate on every trade your invitees make.</p>
                    </div>

                    {/* Referral Link + Code */}
                    <div className="referral-card">
                        <label className="referral-field-label">Referral Link</label>
                        <div className="referral-field-row">
                            <span className="referral-field-value">{referralLink.length > 32 ? `${referralLink.slice(0, 20)}...${referralLink.slice(-8)}` : referralLink}</span>
                            <button className="referral-copy-btn" onClick={() => copyToClipboard(referralLink, 'Referral link')}>
                                <Copy size={16} />
                            </button>
                        </div>

                        <label className="referral-field-label" style={{ marginTop: 16 }}>Referral Code</label>
                        <div className="referral-field-row">
                            <span className="referral-field-value">{referralCode}</span>
                            <button className="referral-copy-btn" onClick={() => copyToClipboard(referralCode, 'Referral code')}>
                                <Copy size={16} />
                            </button>
                        </div>

                        <div className="referral-share-row">
                            <button className="referral-share-link-btn" onClick={shareLink}>
                                <Share2 size={16} /> Share Link
                            </button>
                            <button className="referral-share-qr-btn" onClick={() => setShowQrModal(true)}>
                                <QrCode size={16} /> Share QR Code
                            </button>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="referral-stats-card">
                        <div className="referral-stat-row">
                            <span className="referral-stat-label">My invitees</span>
                            <span className="referral-stat-value">{totalInvitees}</span>
                        </div>
                        <div className="referral-stat-row">
                            <span className="referral-stat-label">Total Rebate Amount</span>
                            <span className="referral-stat-value">{totalRebateAmount.toFixed(2)}</span>
                        </div>
                    </div>

                    {/* My Referral Data */}
                    <div className="referral-data-card">
                        <h3>My Referral Data</h3>
                        <div className="referral-date-filter">
                            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="referral-date-input" />
                            <span className="referral-date-arrow">→</span>
                            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="referral-date-input" />
                        </div>

                        <div className="referral-table-wrap">
                            <table className="referral-table">
                                <thead>
                                    <tr>
                                        <th>Invitees' UID</th>
                                        <th>Activation Date</th>
                                        <th>Transaction amount</th>
                                        <th>Transaction fee</th>
                                        <th>Rebate ratio</th>
                                        <th>Rebate amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan={6} className="referral-table-empty">Loading...</td></tr>
                                    ) : filteredStats.length === 0 ? (
                                        <tr><td colSpan={6} className="referral-table-empty">No referral data yet — invite a friend to get started.</td></tr>
                                    ) : (
                                        filteredStats.map((row, i) => (
                                            <tr key={i}>
                                                <td>{row.uid}</td>
                                                <td>{new Date(row.activationDate).toLocaleDateString()}</td>
                                                <td>{Number(row.transactionAmount).toFixed(2)} USDT</td>
                                                <td>{Number(row.transactionFee).toFixed(2)} USDT</td>
                                                <td>{row.rebateRatio}%</td>
                                                <td>{Number(row.rebateAmount).toFixed(2)} USDT</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Feature highlights */}
                    <div className="referral-features-section">
                        <h3>Transparent and easy-to-use dashboard</h3>
                        {FEATURES.map((f, i) => (
                            <div key={i} className="referral-feature-card">
                                <div className="referral-feature-icon"><f.icon size={20} /></div>
                                <div>
                                    <div className="referral-feature-title">{f.title}</div>
                                    <div className="referral-feature-desc">{f.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Rules */}
                    <div className="referral-rules-section">
                        {RULES.map((rule, i) => (
                            <div key={i} className="referral-rule-row">
                                <span className="referral-rule-num">{String(i + 1).padStart(2, '0')}/</span>
                                <p className="referral-rule-text">{rule}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* QR Modal */}
                {showQrModal && (
                    <div className="referral-qr-overlay" onClick={() => setShowQrModal(false)}>
                        <div className="referral-qr-modal" onClick={e => e.stopPropagation()}>
                            <button className="referral-qr-close" onClick={() => setShowQrModal(false)}><X size={18} /></button>
                            <h3>Scan to join Bicoin</h3>
                            <div className="referral-qr-image-wrap">
                                <img
                                    src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(referralLink)}&bgcolor=ffffff&color=000000`}
                                    alt="Referral QR Code"
                                    width={220}
                                    height={220}
                                />
                            </div>
                            <span className="referral-qr-code-text">{referralCode}</span>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    )
}
